import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom'
import { AppDispatch, RootState } from '../redux-stores/store';
import Avatar from 'react-avatar';
import SocialAccountStats from '../components/influencer/SocialAccountStats';
import YTAccountStat from '../components/influencer/YTAccountStats';
import { Link } from 'react-router-dom';
import { INIT_IG_AUTH_ROUTE } from '../constants/constants';
import IgConnectBenefits from '../components/igConnect/IgConnectBenefits';
import InfluencerSocialContent from '../components/influencer/InfluencerSocialContent';
import usePageTitle from '../hooks/usePageTitle';
import Loader from '../components/loaders/Loader';
import NotFound from './system/NotFound';

const SocialAccount: React.FC = () => {
    usePageTitle("Account Details")
    const { socialAccountId } = useParams();

    const socialAccounts = useSelector((state: RootState) => state.influencer.social_accounts);
    const isLoading = useSelector((state: RootState) => state.influencer.loading);

    const socialAccount = socialAccounts.find((account) => account.social_account.uuid === socialAccountId);

    const platform = socialAccount?.social_account.platform;
    const [viewIGConnection, setIGConnectionVisibility] = useState(false);
    const nicheList = useSelector((state: RootState) => state.auth.master?.niche_list);
    const niches = nicheList ?? {};
    const dispatch: AppDispatch = useDispatch();


    if(isLoading) return <Loader isLoading={isLoading} />

    if(!isLoading && !socialAccount) return <NotFound />

    return (
        <>
            <div className="card card-style overflow-visible p-3">
                <div className="row mb-0">
                    <div className="col-xl-12">
                        <div onClick={(e) => e.stopPropagation()}>
                            {socialAccount ? (
                                <React.Fragment>
                                    <div className="">
                                        {/* Profile Section */}
                                        <div className="d-flex align-items-center position-relative mb-3">
                                            {
                                                platform === 'ig' &&
                                                <div
                                                    className="symbol symbol-60px symbol-circle position-relative rounded-xl"
                                                    style={{ borderColor: "#E24A63", border: '2px solid #E24A63' }}
                                                >
                                                    <img src={socialAccount?.social_account?.profile_picture_url} className="rounded-xl" width={50} alt="Profile" />
                                                    <i
                                                        className="fa-brands fa-instagram icon icon-xs rounded-sm shadow-l bg-instagram"
                                                        style={{ position: "absolute", bottom: "0px", right: "0px" }}
                                                    />
                                                </div>
                                            }
                                            {
                                                platform === 'yt' && socialAccount?.social_account?.yt &&
                                                <div
                                                    className="symbol symbol-60px symbol-circle position-relative rounded-xl"
                                                    style={{ borderColor: "#E24A63", border: '2px solid #E24A63' }}
                                                >
                                                    {
                                                        socialAccount?.social_account?.profile_picture_url ? (
                                                            <img src={(socialAccount?.social_account?.profile_picture_url ?? '').split("=")[0]} className="rounded-xl" width={50} alt="Profile" />
                                                        ) : (
                                                            <Avatar round size='50' name={socialAccount?.social_account?.yt?.title ?? socialAccount?.social_account?.handle} />
                                                        )
                                                    }
                                                    <div className='yt-icon-wrapper'>
                                                        <i className="fa-brands fa-youtube text-danger"></i>
                                                    </div>
                                                </div>
                                            }

                                            <div className="d-flex flex-wrap flex-column ps-2 flex-fill">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex flex-column">
                                                        <a
                                                            href={`${platform === 'ig' ? 'https://instagram.com/' : 'https://youtube.com/@'}${socialAccount?.social_account?.handle}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="color-theme text-left fw-bold"
                                                        >
                                                            @{socialAccount?.social_account?.handle}
                                                        </a>
                                                    </div>

                                                    {/* Connection Status */}
                                                    {
                                                        platform === 'ig' &&
                                                        <div className="d-flex align-items-center gap-1">
                                                            {
                                                                socialAccount?.status === 'connected' ? (
                                                                    <span className="d-inline-block rounded py-0 px-2 bg-success text-white font-11">
                                                                        <small>Connected</small>
                                                                    </span>
                                                                ) : socialAccount?.status === 'verified' ? (
                                                                    <span className="d-inline-block rounded py-0 px-2 bg-info text-white font-11">
                                                                        <small>Verified</small>
                                                                    </span>
                                                                ) : socialAccount.status === 'unverified' ? (
                                                                    <span className="d-inline-block rounded py-0 px-2 bg-red-light text-white font-11">
                                                                        <small>Unverified</small>
                                                                    </span>
                                                                ) : (
                                                                    <span className="d-inline-block rounded py-0 px-2 bg-red-light text-white font-11">
                                                                        <small>Re-Connect</small>
                                                                    </span>
                                                                )
                                                            }
                                                        </div>
                                                    }
                                                </div>

                                                <div className="text-muted font-12" style={{ lineHeight: '1.2rem' }}>
                                                    {
                                                        (() => {
                                                            const text = platform === 'ig'
                                                                ? socialAccount?.social_account?.ig?.biography
                                                                : platform === 'yt'
                                                                    ? socialAccount?.social_account?.yt?.description
                                                                    : null;

                                                            if (!text) return null;

                                                            return text.length > 200 ? text.substring(0, 200) + '...' : text;
                                                        })()
                                                    }
                                                </div>
                                            </div>

                                        </div>
                                        {
                                            (socialAccount?.social_account?.niches ?? []).length > 0 &&
                                            <div className="d-flex justify-content-start flex-wrap align-items-center mb-3 position-relative">
                                                {
                                                    socialAccount?.social_account?.niches?.map((niche, i) => {
                                                        if (!niches[niche]) return null;
                                                        return (
                                                            <span key={i} className="border rounded-pill border-gray-300 mt-2 me-2 py-75 px-2 font-12" >{niches[niche]}</span>
                                                        )
                                                    })
                                                }
                                            </div>
                                        }


                                        {/* Stats Section */}
                                        {
                                            platform === 'ig' ? (
                                                <SocialAccountStats
                                                    mediaCount={socialAccount?.social_account?.ig?.media_count ?? 0}
                                                    followersCount={socialAccount?.social_account?.ig?.followers_count ?? 0}
                                                    followsCount={socialAccount?.social_account?.ig?.follows_count ?? 0}
                                                    engagementScore={Number(socialAccount?.social_account?.engagement_score) ?? 0}
                                                    engagementRate={Number(socialAccount?.social_account?.engagement_rate) ?? 0}
                                                    engagementRateDetails={socialAccount?.social_account?.engagement_rate_details ?? null}
                                                />
                                            ) : platform === 'yt' ? (
                                                <YTAccountStat
                                                    videosCount={socialAccount?.social_account?.yt?.video_count ?? 0}
                                                    subscribersCount={socialAccount?.social_account?.yt?.subscriber_count ?? 0}
                                                    viewsCount={socialAccount?.social_account?.yt?.view_count ?? 0}
                                                    engagementScore={Number(socialAccount?.social_account?.engagement_score) ?? 0}
                                                    engagementRate={Number(socialAccount?.social_account?.engagement_rate) ?? 0}
                                                    engagementRateDetails={socialAccount?.social_account?.engagement_rate_details ?? null}
                                                />
                                            ) : null
                                        }

                                        {
                                            platform === 'ig' && Object.values(socialAccount.permissions ?? {}).length > 0 && socialAccount.status === 'connected' &&
                                            <div className="mx-3">
                                                <div className="list-group list-custom-small list-icon-0">
                                                    <a onClick={() => setIGConnectionVisibility(!viewIGConnection)} data-bs-toggle="collapse" className="no-effect">
                                                        <span className="font-14">Permissions</span>
                                                        <i className="fa fa-angle-down"></i>
                                                    </a>
                                                </div>
                                                <div className={`collapse ${viewIGConnection ? 'show' : 'hidden'}`} id="collapse-1">
                                                    <div className="list-group list-custom-small ps-3">
                                                        {
                                                            Object.values(socialAccount.permissions ?? {}).map((permission, i) => <a key={i} href="#">
                                                                <i className="fa-solid fa-circle-check text-success"></i>
                                                                <span>{permission}</span>
                                                            </a>)
                                                        }
                                                    </div>
                                                    <Link className="btn btn-outline-primary reconnect-btn w-100 rounded-4 my-2 d-flex gap-2 justify-content-center align-items-center font-14 fw-bold" to={INIT_IG_AUTH_ROUTE + '?force=y&' + 'social_account_id=' + socialAccount.social_account.uuid}><i className="fa-solid fa-rotate"></i>Refresh</Link>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                    {
                                        platform === 'ig' && socialAccount?.status !== 'connected' &&
                                        <React.Fragment>
                                            <div className="divider mb-3"></div>
                                            <div className="content my-0">
                                                <h4 className="pop">
                                                    {['verified', 'unverified'].includes(socialAccount.status) ? 'Connect' : 'Reconnect'} your Instagram to get premium collabs.
                                                </h4>
                                                <IgConnectBenefits />
                                                <Link to={INIT_IG_AUTH_ROUTE + '?social_account_id=' + socialAccount.social_account.uuid} className="btn btn-sm btn-primary rounded-sm w-100 my-2">Connect with Instagram</Link>
                                            </div>
                                        </React.Fragment>
                                    }
                                </React.Fragment>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
            <h3 className='mx-3'>Content</h3>
            {
                socialAccount &&
                <div className="content">
                    <InfluencerSocialContent socialAccount={socialAccount} />
                </div>
            }
        </>

    )
}

export default SocialAccount
